import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  ContentControlAntiphishingDefaultAction,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import deepmerge from 'deepmerge';
import {
  CONTENT_CONTROL_DATA_PROTECTION_DEFAULT_VALUE,
  getContentControlDataProtectionSearchableKeywords,
  getDefaultActionFormOptions,
} from '../../../../utils/uiSettings/contentControl/content-control.values';
import {
  ContentControlDataProtectionFormValue,
  ContentControlDataProtectionSearchableKeywords,
} from '../../../../utils/uiSettings/contentControl/content-control.types';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { Feature } from '@nirvana/shared/constants';
import { classMap } from 'lit-html/directives/class-map.js';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION)
export class GzContentControlDataProtectionWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ContentControlDataProtectionFormValue,
  ContentControlDataProtectionSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.CONTENT_CONTROL,
    sectionId: SectionId.CONTENT_CONTROL_DATA_PROTECTION,
  };
  protected searchableKeywords = getContentControlDataProtectionSearchableKeywords();
  private isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);

  @query('#content-control-antiphishing-enable-field')
  private antiphishingEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#content-control-antiphishing-info-text')
  private antiphishingInfoText: HTMLElement;
  @query('#content-control-antiphishing-content')
  private antiphishingContent: HTMLElement;
  @query('#content-control-antiphishing-default-action-field')
  private antiphishingDefaultActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#content-control-antiphishing-protect-against-fraud-field')
  private antiphishingProtectAgainstFraudField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-antiphishing-protect-against-phishing-field')
  private antiphishingProtectAgainstPhishingField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#content-control-traffic-scan-web-field')
  private trafficScanWebField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#content-control-traffic-scan-web-content')
  private trafficScanWebContent: HTMLElement;
  @query('#content-control-traffic-scan-email-field')
  private trafficScanEmailField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#content-control-traffic-scan-email-content')
  private trafficScanEmailContent: HTMLElement;
  @query('#content-control-traffic-scan-email-incoming-pop3-field')
  private trafficScanEmailIncomingPop3Field: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-email-incoming-imap-field')
  private trafficScanEmailIncomingImapField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-email-outgoing-smtp-field')
  private trafficScanEmailOutgoingSmtpField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-email-incoming-and-outgoing-mapi-field')
  private trafficScanEmailIncomingAndOutgoingMapiField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="content-control-antiphishing-enable-field" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Antiphishing].label}"
        gz-form-input
        @toggleChange="${this.onAntiphisingToggleChange}"
      >
        <span
          >${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Antiphishing].translatedLabel}</span
        >
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="content-control-antiphishing-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Antiphishing blocks known phishing webpages, preventing users from inadvertently disclosing private or confidential information to online fraudsters.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    const classes = classMap({
      hidden: this.isCompanionStandalone,
    });

    return html`
      <div id="content-control-antiphishing-content">
        <gz-form-field
          class="label-mw-250 mt-2"
          id="content-control-antiphishing-default-action-field"
          searchable-label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.DefaultAction]
            .label}"
          label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.DefaultAction]
            .translatedLabel}"
        >
          <gz-dropdown gz-form-input options="${JSON.stringify(getDefaultActionFormOptions())}"></gz-dropdown>
        </gz-form-field>

        <gz-form-field class="label-mw-250" id="content-control-antiphishing-protect-against-fraud-field">
          <gz-checkbox
            searchable-label="${this.searchableKeywords[
              ContentControlDataProtectionSearchableKeywords.ProtectionAgainstFraud
            ].label}"
            label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.ProtectionAgainstFraud]
              .translatedLabel}"
            gz-form-input
            @change="${this.checkAntiphishingPanelStatusForProtectionAgainstFraud.bind(this)}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field class="label-mw-250" id="content-control-antiphishing-protect-against-phishing-field">
          <gz-checkbox
            searchable-label="${this.searchableKeywords[
              ContentControlDataProtectionSearchableKeywords.ProtectionAgainstPhishing
            ].label}"
            label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.ProtectionAgainstPhishing]
              .translatedLabel}"
            gz-form-input
            @change="${this.checkAntiphishingPanelStatusForProtectionAgainstPhishing.bind(this)}"
          ></gz-checkbox>
        </gz-form-field>
      </div>

      <div>
        <gz-form-field id="content-control-traffic-scan-web-field" class="mt-4 mbw-1" status-field>
          <gz-toggle
            class="content-header-toggle"
            searchable-label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.WebTrafficScan]
              .label}"
            gz-form-input
            @toggleChange="${e => toggleDisabled(this.trafficScanWebContent, !e.detail.checked)}"
          >
            <span
              >${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.WebTrafficScan]
                .translatedLabel}</span
            >
          </gz-toggle>
        </gz-form-field>

        <div id="content-control-traffic-scan-web-content">
          <gz-text fontSize="12" fontWeight="400" class="infoText"
            >${this.isCompanionStandalone
              ? translateFn(
                  'Web Traffic Scan analyzes all inbound HTTP traffic in real time and detects malicious payloads being downloaded into your network.'
                )
              : translateFn(
                  'Web Traffic Scan analyzes all inbound HTTP traffic in real time, preventing malicious payloads from being downloaded into your network.'
                )}
          </gz-text>
        </div>
      </div>

      <div class="${classes}">
        <gz-form-field id="content-control-traffic-scan-email-field" class="mt-4 mbw-1" status-field>
          <gz-toggle
            class="content-header-toggle"
            searchable-label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.EmailTrafficScan]
              .label}"
            gz-form-input
            @toggleChange="${this.onEmailTrafficToggleChange.bind(this)}"
          >
            <span
              >${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.EmailTrafficScan]
                .translatedLabel}</span
            >
          </gz-toggle>
        </gz-form-field>

        <div id="content-control-traffic-scan-email-content">
          <gz-text fontSize="12" fontWeight="400" class="infoText" id="content-control-traffic-scan-text"
            >${translateFn(
              'Email Traffic Scan inspects incoming (POP3, IMAP) and outgoing (SMTP) emails in real time to prevent malware. Infected emails are replaced with notifications to the recipients. MAPI traffic is monitored and reported only.'
            )}
          </gz-text>

          <gz-form-field class="label-mw-250 mt-2" id="content-control-traffic-scan-email-incoming-pop3-field" no-impact>
            <gz-checkbox
              searchable-label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Pop3].label}"
              label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Pop3].translatedLabel}"
              gz-form-input
              @change="${this.checkScanEmailStatusForIncomingPop3.bind(this)}"
            ></gz-checkbox>
          </gz-form-field>

          <gz-form-field class="label-mw-250" id="content-control-traffic-scan-email-incoming-imap-field" no-impact>
            <gz-checkbox
              searchable-label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Imap].label}"
              label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Imap].translatedLabel}"
              gz-form-input
              @change="${this.checkScanEmailStatusForIncomingImap.bind(this)}"
            ></gz-checkbox>
          </gz-form-field>

          <gz-form-field class="label-mw-250" id="content-control-traffic-scan-email-outgoing-smtp-field" no-impact>
            <gz-checkbox
              searchable-label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Smtp].label}"
              label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Smtp].translatedLabel}"
              gz-form-input
              @change="${this.checkScanEmailStatusForOutgoingSmtp.bind(this)}"
            ></gz-checkbox>
          </gz-form-field>

          <gz-form-field
            class="label-mw-250" id="content-control-traffic-scan-email-incoming-and-outgoing-mapi-field"
            ?disabled="${this.shouldMapiBeDisabled()}"
            no-impact>
              <gz-checkbox
                searchable-label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Mapi].label}"
                label="${this.searchableKeywords[ContentControlDataProtectionSearchableKeywords.Mapi].translatedLabel}"
                gz-form-input
                @change="${this.checkScanEmailStatusForIncomingAndOutgoingMapi.bind(this)}"
              ></gz-checkbox>
              <gz-info-box
                id="content-control-traffic-scan-email-incoming-and-outgoing-mapi-field-info-box"
                severity='warning'
                class="mt-2 ${!this.shouldMapiBeDisabled() ? 'hidden' : ''}
              ">
                ${translateFn('To enable Incoming and outgoing (MAPI), first activate encrypted MAPI interception in the General section.')}
              </gz-info-box>
          </gz-form-field>

          <!-- TODO: Add warning text when MAPI is disabled -->

        </div>
      </div>
    `;
  }

  private shouldMapiBeDisabled() {
    const isMapiEnabled: boolean = PolicyPanelManager.getInstance().getPolicy(
      'uiSettings.general.advanced.settings.scanSsl.protocols.outgoing.mapi'
      , false);
    return !isMapiEnabled;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.contentControl
   */
  protected getValueFromManager(): ContentControlDataProtectionFormValue {
    // get default value
    const defaultValue = CONTENT_CONTROL_DATA_PROTECTION_DEFAULT_VALUE;
    const policyManagerInstance = PolicyPanelManager.getInstance();

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.CONTENT_CONTROL]: {
          antiphishing: policyManagerInstance.getPolicy('uiSettings.contentControl.antiphishing'),
          [SectionId.CONTENT_CONTROL_TRAFFIC]: {
            trafficScan: policyManagerInstance.getPolicy('uiSettings.contentControl.traffic.trafficScan'),
          },
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    // disable antiphishing default action field if companion
    toggleDisabled(this.antiphishingDefaultActionField, this.isCompanionStandalone);

    this.formGroup = FormGroup.build({
      uiSettings: {
        contentControl: {
          antiphishing: {
            enable: this.antiphishingEnableField,
            defaultAction: this.antiphishingDefaultActionField,
            settings: {
              protectionAgainstFraud: this.antiphishingProtectAgainstFraudField,
              protectionAgainstPhishing: this.antiphishingProtectAgainstPhishingField,
            },
          },
          traffic: {
            trafficScan: {
              incomingEmails: {
                pop3: this.trafficScanEmailIncomingPop3Field,
                imap: this.trafficScanEmailIncomingImapField
              },
              outgoingEmails: {
                smtp: this.trafficScanEmailOutgoingSmtpField
              },
              incomingAndOutgoingEmails: {
                mapi: this.trafficScanEmailIncomingAndOutgoingMapiField
              },
              webTraffic: this.trafficScanWebField,
              emailTraffic: this.trafficScanEmailField,
            },
          },
        },
      },
    });
  }

  /**
   * Get fixed antiphishing enable state.
   * If protection against fraud and phishing are disabled, enable state should be false.
   * Old version of policy accepts this state and resets it to false if it is corrupted.
   * @private
   */
  private getFixedAntiphisingEnableState() {
    const isCorrupted =
      this.valueT?.uiSettings.contentControl.antiphishing.enable &&
      !this.valueT?.uiSettings.contentControl.antiphishing.settings.protectionAgainstFraud &&
      !this.valueT?.uiSettings.contentControl.antiphishing.settings.protectionAgainstPhishing;

    return isCorrupted ? false : this.valueT?.uiSettings.contentControl.antiphishing.enable;
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        contentControl: {
          antiphishing: {
            enable: this.getFixedAntiphisingEnableState(),
            defaultAction: this.isCompanionStandalone
              ? ContentControlAntiphishingDefaultAction.REPORT_ONLY
              : this.valueT.uiSettings.contentControl.antiphishing.defaultAction, // enforce default action to REPORT_ONLY if companion
            settings: this.valueT.uiSettings.contentControl.antiphishing.settings,
          },
          traffic: {
            trafficScan: this.valueT.uiSettings.contentControl.traffic.trafficScan,
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // use old value for default action if companion. Discard value from form group.
    if (this.isCompanionStandalone) {
      _.set(
        value,
        ['uiSettings', 'contentControl', 'antiphishing', 'defaultAction'],
        this.valueT?.uiSettings.contentControl.antiphishing.defaultAction
      );
    }

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'contentControl'], {});
    }

    return value;
  }

  onAntiphisingToggleChange(event: CustomEvent) {
    const value = event.detail.checked;
    toggleDisabled(this.antiphishingContent, !event.detail.checked);
    toggleDisabled(this.antiphishingInfoText, !event.detail.checked);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.antiphishingProtectAgainstFraudField.value = value;
    this.antiphishingProtectAgainstPhishingField.value = value;
  }

  checkAntiphishingPanelStatusForProtectionAgainstFraud() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkAntiphishingPanelStatus();
  }

  checkAntiphishingPanelStatusForProtectionAgainstPhishing() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkAntiphishingPanelStatus();
  }

  checkAntiphishingPanelStatus() {
    const protectionAgainstFraud = this.antiphishingProtectAgainstFraudField.value;
    const protectionAgainstPhishing = this.antiphishingProtectAgainstPhishingField.value;

    if (protectionAgainstFraud || protectionAgainstPhishing) {
      return;
    }

    if (!this.antiphishingEnableField.value) {
      return;
    }

    this.antiphishingEnableField.value = false;
  }

  onEmailTrafficToggleChange(event: CustomEvent) {
    const value = event.detail.checked;
    toggleDisabled(this.trafficScanEmailContent, !value);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.trafficScanEmailIncomingPop3Field.value = value;
    this.trafficScanEmailIncomingImapField.value = value;
    this.trafficScanEmailOutgoingSmtpField.value = value;

    // set value of imap checkbox if email traffic toggle is unchecked or imap checkbox isn't disabled
    if (!value || !this.trafficScanEmailIncomingAndOutgoingMapiField.disabled) {
      this.trafficScanEmailIncomingAndOutgoingMapiField.value = value;
    }
  }

  checkScanEmailStatusForIncomingPop3() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkScanEmailStatus();
  }

  checkScanEmailStatusForIncomingImap() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkScanEmailStatus();
  }

  checkScanEmailStatusForIncomingAndOutgoingMapi() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkScanEmailStatus();
  }

  checkScanEmailStatusForOutgoingSmtp() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }
  }

  checkScanEmailStatus() {
    const pop3 = this.trafficScanEmailIncomingPop3Field.value;
    const smtp = this.trafficScanEmailOutgoingSmtpField.value;
    const imap = this.trafficScanEmailIncomingImapField.value;
    const mapi = this.trafficScanEmailIncomingAndOutgoingMapiField.value;

    if (pop3 || smtp || imap || mapi) {
      return;
    }

    if (!this.trafficScanEmailField.value) {
      return;
    }

    this.trafficScanEmailField.value = false;
  }

  public reset() {
    super.reset();
    this.antiphishingEnableField.setAttribute('no-impact', '');
    this.antiphishingProtectAgainstFraudField.setAttribute('no-impact', '');
    this.antiphishingProtectAgainstPhishingField.setAttribute('no-impact', '');
    this.trafficScanEmailField.setAttribute('no-impact', '');
    this.trafficScanEmailIncomingPop3Field.setAttribute('no-impact', '');
    this.trafficScanEmailIncomingImapField.setAttribute('no-impact', '');
    this.trafficScanEmailOutgoingSmtpField.setAttribute('no-impact', '');
    this.trafficScanEmailIncomingAndOutgoingMapiField.setAttribute('no-impact', '');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION: GzContentControlDataProtectionWebComponent;
  }
}
